// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container, Row, Col } from 'reactstrap';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import SelfCheckInHeader from 'components/atoms/SelfCheckInHeader/';
import SCIEditServicesList from 'components/molecules/SCIEditServicesList';
import SCITellUsMore from 'components/atoms/SCITellUsMore';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import {
  SCI_SERVICES_ROUTE,
  SCI_ADVISOR_ROUTE,
  SCI_CONFIRMATION_ROUTE,
  WAITING_OPTIONS,
  SELF_CHECK_IN_FLOW,
  getAdditionalSelfCheckInServices
} from 'constants/selfCheckIn';
import { NAVIGATION } from 'constants/navigations';
import { labelsStepIndicatorSCI } from 'constants/CTAConfig';

import './EditServicesPage.scss';

const { FORWARD } = NAVIGATION;
const { GENERIC_FLOW } = SELF_CHECK_IN_FLOW;
const { IN_WAITING_AREA_ON_SERVICES } = WAITING_OPTIONS;

export type Props = {
  intl: IntlShape,
  goTo: (path: string, direction?: string) => void,
  services: Array<Object>,
  selfCheckInFlow: string,
  checkIn: (waitingOption: string) => void,
  showSCIProgressBar: boolean
};

type State = {
  isGenericFlow: boolean
};

class EditServicesPage extends React.Component<Props, State> {
  static block = 'edit-services-page';

  constructor(props: Props) {
    super(props);
    this.getBody = this.getBody.bind(this);
    this.getTitle = this.getTitle.bind(this);
    this.state = {
      isGenericFlow: props.selfCheckInFlow === GENERIC_FLOW
    };
  }

  makeCheckIn = async () => {
    const { checkIn, goTo } = this.props;
    await checkIn(IN_WAITING_AREA_ON_SERVICES);
    goTo(SCI_CONFIRMATION_ROUTE);
  };

  getTitle = () => {
    const { intl } = this.props;
    const { isGenericFlow } = this.state;
    const ns = EditServicesPage.block;

    return !isGenericFlow ? (
      <h1 className={`${ns}__msg mb-4`}>
        {intl.formatMessage({
          id: 'c8.self.check.in.edit.service.page.need.additional.services',
          defaultMessage: 'Need additional services?'
        })}
      </h1>
    ) : (
      <p className={`${ns}__label`}>
        {intl.formatMessage({
          id: 'c8.self.check.in.edit.service.page.final.label'
        })}
      </p>
    );
  };

  getBody = () => {
    const { intl } = this.props;
    const { isGenericFlow } = this.state;
    const services = getAdditionalSelfCheckInServices(intl);
    const ns = EditServicesPage.block;
    const formattedServices = services.map((service, index) => ({
      id: index,
      name: intl.formatMessage({ id: service })
    }));
    const waitingArea = intl.formatMessage({
      id: 'c8.self.check.in.config.customer.waiting.area',
      defaultMessage: 'Waiting area'
    });

    return !isGenericFlow ? (
      <React.Fragment>
        <Row>
          <Col>
            <SCIEditServicesList
              serviceList={formattedServices}
              showSelectAll={false}
              addToTellUsMore={true}
              group={'selfCheckInEditServices'}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <SCITellUsMore />
          </Col>
        </Row>
      </React.Fragment>
    ) : (
      <Row>
        <Col>
          <h1 className={`${ns}__msg ${ns}__msg--small`}>
            {intl.formatMessage(
              {
                id: 'c8.self.check.in.edit.service.page.final.title'
              },
              {
                waitingArea
              }
            )}
          </h1>
        </Col>
      </Row>
    );
  };

  render() {
    const { goTo, intl, showSCIProgressBar } = this.props;
    const { isGenericFlow } = this.state;
    const ns = EditServicesPage.block;
    const footerLabel = intl.formatMessage({
      id: isGenericFlow
        ? 'c8.self.check.in.common.complete.final.check.in'
        : 'c8.self.check.in.footer.next'
    });
    const onClickAction = isGenericFlow
      ? () => this.makeCheckIn()
      : () => goTo(SCI_ADVISOR_ROUTE, FORWARD);

    return (
      <AppointmentFlowWizard
        isSelfCheckIn={true}
        className={ns}
        showSCIProgressBar={showSCIProgressBar}
        header={() =>
          showSCIProgressBar ? (
            <CtaStepIndicator
              htmlId="VehiclePageStepIndicator"
              labels={labelsStepIndicatorSCI(intl)}
              activeStep={2}
              lastStep={isGenericFlow ? 2 : 4}
            />
          ) : (
            <SelfCheckInHeader
              label={intl.formatMessage({
                id: 'c8.self.check.in.header.services'
              })}
              route={SCI_SERVICES_ROUTE}
              ariaLabel={intl.formatMessage({
                id: 'c8.aria.back.to.services',
                defaultMessage: 'Back to services'
              })}
            />
          )
        }
        footer={() => (
          <AppointmentFlowFooter
            buttonText={footerLabel}
            onClick={onClickAction}
            className={isGenericFlow ? `${ns}__footer-button` : ''}
            ariaLabel={intl.formatMessage({
              id: 'c8.self.check.in.common.complete.check.in'
            })}
          />
        )}
        body={() => (
          <React.Fragment>
            {showSCIProgressBar && (
              <SelfCheckInHeader
                label={intl.formatMessage({
                  id: 'c8.self.check.in.header.services'
                })}
                route={SCI_SERVICES_ROUTE}
                ariaLabel={intl.formatMessage({
                  id: 'c8.aria.back.to.services',
                  defaultMessage: 'Back to services'
                })}
              />
            )}
            <Container>
              <Row>
                <Col>{this.getTitle()}</Col>
              </Row>
              {this.getBody()}
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default EditServicesPage;



// WEBPACK FOOTER //
// ./src/components/pages/SelfCheckIn/EditServicesPage/EditServicesPage.js